
import { defineComponent, ref, computed, watch } from 'vue';
import { ComponentName } from '@/products/api/configuration/components/ComponentName';
import { DataType } from '@/products/api/configuration/application/DataType';
import { setupComponent } from '@/products/composables/setupComponent';
import { ProductItem, ProductVariantGroup } from '@/common/services/swagger/index.defs';

import InjectStyles from '@/common/components/InjectStyles.vue';
import Dropdown from '@/common/components/Dropdown.vue';
import Clickable from '@/common/components/Clickable.vue';
import { DetailsCommand } from '@/products/api/runtime/CommandExecutor';
import { IDetailsVariantsDropdownOptions } from '@/products/api/configuration/components/IDetailsVariantsDropdownOptions';
import { attrValue } from '@/common/helpers/productAttributes';

interface DropdownItem {
  type: 'group' | 'product';
  value?: ProductVariantGroup | ProductItem;
}

export default defineComponent({
  name: ComponentName.detailsVariantsDropdown,

  components: {
    InjectStyles,
    Dropdown,
    Clickable,
  },

  props: {
    instanceId: String,
  },

  setup(props) {
    const root = ref<HTMLElement>();
    const isDropdownOpen = ref(false);

    const { componentName, isReady, isWebComponent, instance, store, t, routeData } =
      setupComponent(root, props.instanceId, [DataType.ProductVariantsDropdown]);

    const componentOptions = computed(
      () =>
        store.value?.options.components?.detailsVariantsDropdown ??
        ({} as IDetailsVariantsDropdownOptions),
    );

    const variants = computed(() => {
      if (!store.value?.data.productVariantsDropdown?.items?.length) return [];

      return store.value?.data.productVariantsDropdown.items;
    });

    const visible = computed(() => {
      return componentOptions.value.visible !== false && (variants.value?.length ?? 0) > 0;
    });

    const dropdownItems = computed((): DropdownItem[] => {
      const items: DropdownItem[] = [];

      if (!variants.value?.length) return items;

      variants.value.forEach((variant) => {
        // Add group header
        items.push({
          type: 'group',
          value: variant,
        });

        variant.products?.forEach((value) => {
          // Add products in value
          items.push({
            type: 'product',
            value: value,
          });
        });
      });

      return items;
    });

    const details = async (productItem: ProductItem) => {
      if (productItem?.inactive) return;

      if (routeData.value && productItem) {
        isDropdownOpen.value = false;
        try {
          await instance.value?.execute(new DetailsCommand(productItem));
        } catch (error) {
          // Ignore Error
        }
      }
    };

    const toggleDropdown = () => {
      isDropdownOpen.value = !isDropdownOpen.value;
    };

    const selectedProduct = computed(() => {
      return store.value?.data.productDetail?.item || ({} as ProductItem);
    });

    return {
      root,
      componentName,
      isReady,
      isWebComponent,
      instance,
      store,
      t,
      componentOptions,
      variants,
      visible,
      isDropdownOpen,
      selectedProduct,
      dropdownItems,
      attrValue,
      toggleDropdown,
      details,
    };
  },
});
